import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import i18n from '@/i18n'
import { useProfileStore as useCoreProfileStore } from '@flows.cash/core'
import { useAuthStore } from '@/store/authStore'

interface LanguageStore {
  language: string
  setLanguage: (lang: string) => void
}

const getInitialLanguage = () => {
  // First check profile store
  const profileStore = useCoreProfileStore.getState()
  if (profileStore.settings?.preferences?.language) {
    const profileLang = profileStore.settings.preferences.language
    i18n.changeLanguage(profileLang)
    document.documentElement.dir = profileLang === 'ar' ? 'rtl' : 'ltr'
    return profileLang
  }

  // Then check i18next language
  const i18nextLang = localStorage.getItem('i18nextLng')
  if (i18nextLang) {
    i18n.changeLanguage(i18nextLang)
    document.documentElement.dir = i18nextLang === 'ar' ? 'rtl' : 'ltr'
    return i18nextLang
  }

  return 'en'
}

export const useLanguage = create<LanguageStore>()(
  persist(
    (set) => ({
      language: getInitialLanguage(),
      setLanguage: (lang: string) => {
        // Change i18n language
        i18n.changeLanguage(lang)
        
        // Update document direction for RTL languages
        if (lang === 'ar') {
          document.documentElement.dir = 'rtl'
        } else {
          document.documentElement.dir = 'ltr'
        }
        
        // Update profile store if user is logged in
        const authStore = useAuthStore.getState()
        const profileStore = useCoreProfileStore.getState()
        if (authStore.user?.id && profileStore.settings) {
          profileStore.updateSettings(authStore.user.id, {
            preferences: {
              ...profileStore.settings.preferences,
              language: lang,
            },
          })
        }
        
        // Update i18next storage
        localStorage.setItem('i18nextLng', lang)
        
        // Update store
        set({ language: lang })
        
        // Special handling for Turkish language due to caching issues
        if (lang === 'tr') {
          console.log('Turkish language selected, forcing reload to ensure proper translation');
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
      },
    }),
    {
      name: 'flows-language-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) {
          i18n.changeLanguage(state.language)
          document.documentElement.dir = state.language === 'ar' ? 'rtl' : 'ltr'
        }
      },
    }
  )
)
